import PropTypes from 'prop-types';
import { Box, FormControl, FormHelperText, FormLabel, Typography } from '@mui/material';
import { useFormContext, Controller } from 'react-hook-form';
import Iconify from 'src/components/iconify';
import { resolveModifierThumbUrl } from './modifier-demo-images';

// ----------------------------------------------------------------------

const PRIMARY = '#0e7c62';
const PRIMARY_SOFT = 'rgba(14, 124, 98, 0.1)';
const BORDER = 'rgba(0,0,0,0.1)';

function ModifierVisualTile({ selected, label, priceLine, imageUrl, onClick, disabled }) {
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-pressed={selected}
      sx={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        border: '2px solid',
        borderColor: selected ? PRIMARY : BORDER,
        borderRadius: '12px',
        p: 1.5,
        textAlign: 'center',
        bgcolor: selected ? PRIMARY_SOFT : '#fff',
        boxShadow: selected ? `0 0 0 1px ${PRIMARY}` : 'none',
        transition: 'border-color 0.2s, background-color 0.2s, box-shadow 0.2s',
        '&:hover': {
          borderColor: selected ? PRIMARY : 'rgba(14,124,98,0.35)',
          bgcolor: selected ? PRIMARY_SOFT : 'rgba(0,0,0,0.02)',
        },
        '&:focus-visible': {
          outline: `2px solid ${PRIMARY}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          mx: 'auto',
          mb: 1,
          overflow: 'hidden',
          bgcolor: '#f0f0f2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {imageUrl ? (
          <Box component="img" src={imageUrl} alt="" sx={{ width: 1, height: 1, objectFit: 'cover' }} />
        ) : (
          <Iconify icon="solar:bowl-linear" width={28} sx={{ color: 'text.disabled' }} />
        )}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3, color: '#1d1d1f' }}>
        {label}
      </Typography>
      {priceLine ? (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontSize: 12 }}>
          {priceLine}
        </Typography>
      ) : null}
    </Box>
  );
}

ModifierVisualTile.propTypes = {
  selected: PropTypes.bool,
  label: PropTypes.string,
  priceLine: PropTypes.string,
  imageUrl: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
};

function singleSectionLabel(mg, selectedCount) {
  const countPart = `${selectedCount ? 1 : 0}/1`;
  return (
    <>
      {mg?.name || 'Options'} *{' '}
      <Typography component="span" variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
        {countPart}
      </Typography>
    </>
  );
}

function multiSectionLabel(mg, selectedCount) {
  const max = Number(mg?.selection_allowed) || 99;
  return (
    <>
      {mg?.name || 'Options'}{' '}
      <Typography component="span" variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
        {selectedCount}/{max}
      </Typography>
    </>
  );
}

function buildOptions(mg, getPrice) {
  return (mg.modifiers || []).map((m) => ({
    modifier: m,
    value: m.id,
    label: m?.name || '',
    priceLine:
      Number(m?.actual_price || m?.price || mg?.price || 0) > 0
        ? `+${getPrice(m?.actual_price || m?.price || mg?.price || 0)}`
        : '',
    imageUrl: resolveModifierThumbUrl(m),
  }));
}

function groupIdSet(mg) {
  return new Set((mg.modifiers || []).map((m) => String(m.id)));
}

function partitionByGroup(raw, mg) {
  const ids = groupIdSet(mg);
  const inGroup = (raw || []).filter((id) => ids.has(String(id)));
  const outside = (raw || []).filter((id) => !ids.has(String(id)));
  return { inGroup, outside };
}

export default function ProductModifierVisual({ modifierGroups, getPrice }) {
  const { control } = useFormContext();

  if (!modifierGroups?.length) {
    return null;
  }

  const singleGroups = modifierGroups.filter((mg) => mg?.selection_allowed === 1);
  const multiGroups = modifierGroups.filter((mg) => mg?.selection_allowed !== 1);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {singleGroups.map((mg) => {
        const name = `selectedSingleModifier.mg-${mg.id}`;
        const options = buildOptions(mg, getPrice);
        return (
          <Controller
            key={mg.id}
            name={name}
            control={control}
            defaultValue=""
            render={({ field, fieldState: { error } }) => {
              const selectedCount = field.value !== undefined && field.value !== null && field.value !== '' ? 1 : 0;
              return (
                <FormControl component="fieldset" variant="standard" error={!!error} sx={{ width: 1 }}>
                  <FormLabel
                    component="legend"
                    sx={{
                      mb: 1.5,
                      typography: 'subtitle2',
                      fontWeight: 700,
                      color: 'text.primary',
                      '&.Mui-focused': { color: 'text.primary' },
                    }}
                  >
                    {singleSectionLabel(mg, selectedCount)}
                  </FormLabel>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' },
                      gap: 1.5,
                    }}
                  >
                    {options.map((opt) => {
                      const selected = String(field.value) === String(opt.value);
                      return (
                        <ModifierVisualTile
                          key={opt.value}
                          label={opt.label}
                          priceLine={opt.priceLine}
                          imageUrl={opt.imageUrl}
                          selected={selected}
                          onClick={() => field.onChange(selected ? '' : String(opt.value))}
                        />
                      );
                    })}
                  </Box>
                  {error?.message ? <FormHelperText>{error.message}</FormHelperText> : null}
                </FormControl>
              );
            }}
          />
        );
      })}

      {multiGroups.length > 0 && (
        <Controller
          name="selectedModifiers"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <>
              {multiGroups.map((mg) => {
                const options = buildOptions(mg, getPrice);
                const maxPick = Number(mg?.selection_allowed) || 99;
                const raw = field.value || [];
                const { inGroup, outside } = partitionByGroup(raw, mg);
                const selectedIds = inGroup.map((id) => String(id));
                const selectedCount = inGroup.length;

                const toggle = (optValue) => {
                  const idStr = String(optValue);
                  const has = selectedIds.includes(idStr);
                  if (has) {
                    field.onChange([...outside, ...inGroup.filter((id) => String(id) !== idStr)]);
                    return;
                  }
                  if (inGroup.length >= maxPick) {
                    return;
                  }
                  field.onChange([...outside, ...inGroup, optValue]);
                };

                return (
                  <FormControl key={mg.id} component="fieldset" variant="standard" error={!!error} sx={{ width: 1 }}>
                    <FormLabel
                      component="legend"
                      sx={{
                        mb: 1.5,
                        typography: 'subtitle2',
                        fontWeight: 700,
                        color: 'text.primary',
                        '&.Mui-focused': { color: 'text.primary' },
                      }}
                    >
                      {multiSectionLabel(mg, selectedCount)}
                    </FormLabel>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' },
                        gap: 1.5,
                      }}
                    >
                      {options.map((opt) => {
                        const selected = selectedIds.includes(String(opt.value));
                        const atMax = !selected && inGroup.length >= maxPick;
                        return (
                          <ModifierVisualTile
                            key={opt.value}
                            label={opt.label}
                            priceLine={opt.priceLine}
                            imageUrl={opt.imageUrl}
                            selected={selected}
                            disabled={atMax}
                            onClick={() => toggle(opt.value)}
                          />
                        );
                      })}
                    </Box>
                  </FormControl>
                );
              })}
              {error?.message ? <FormHelperText sx={{ mt: 0 }}>{error.message}</FormHelperText> : null}
            </>
          )}
        />
      )}
    </Box>
  );
}

ProductModifierVisual.propTypes = {
  modifierGroups: PropTypes.array,
  getPrice: PropTypes.func.isRequired,
};
